import {useState} from "react";
import {Collapse, Dialog, Divider, useTheme} from "@mui/material";
import {FormattedMessage, defineMessage, defineMessages} from "react-intl";
import {InfoIcon, FormattedButton, DialogActions, FormattedDialogTitle, IconDialogContent} from "@dvrt/graphql-widgets";
import {BackendError, BackendErrorCode} from "../types";

const BACKEND_ERROR_MESSAGES = defineMessages<BackendErrorCode>({
    not_authorized: {
        id: "backendError.codes.not_authorized",
        defaultMessage: "You don't have enough permissions to use this function.",
    },
    unreachable: {id: "backendError.codes.unreachable", defaultMessage: "Network error: the backend is unreachable!"},
    invalid_session: {id: "backendError.codes.invalid_session", defaultMessage: "Invalid session."},
    expired_access_token: {
        id: "backendError.codes.expired_access_token",
        defaultMessage: "Your session expired. Please sign in again.",
    },
    unknown_access_token: {
        id: "backendError.codes.unknown_access_token",
        defaultMessage: "Unknown access token. Please sign in again.",
    },
    cant_reach_heimdall: {
        id: "backendError.codes.cant_reach_heimdall",
        defaultMessage: "The authorization service is not available.",
    },
    unknown: {id: "backendError.codes.unknown", defaultMessage: "Unexpected error."},
});

export type Props = {
    backendError: BackendError;
};

const BackendErrorDialog = (props: Props) => {
    const [showDetails, setShowDetails] = useState(false);
    const theme = useTheme();

    const show = props.backendError !== null;

    return (
        <Dialog open={show}>
            <FormattedDialogTitle title={defineMessage({id: "backendError.title", defaultMessage: "Backend error!"})} />
            <IconDialogContent icon="exclamation-triangle" iconColor={theme.palette.secondary.main}>
                <FormattedMessage {...BACKEND_ERROR_MESSAGES[props.backendError?.code ?? "unknown"]} />
                {!!props.backendError?.details && (
                    <>
                        <InfoIcon
                            onClick={() => setShowDetails(!showDetails)}
                            style={{marginLeft: "0.3em", cursor: "pointer"}}
                        />
                        <Collapse in={showDetails}>
                            <Divider style={{margin: "0.5em 0"}} />
                            <pre style={{maxWidth: 400, whiteSpace: "pre-wrap", overflowWrap: "break-word"}}>
                                {props.backendError?.details}
                            </pre>
                        </Collapse>
                    </>
                )}
            </IconDialogContent>
            <DialogActions>
                <FormattedButton
                    message={defineMessage({id: "backendError.reload", defaultMessage: "Reload the page"})}
                    onClick={() => window.location.reload()}
                    variant="text"
                />
            </DialogActions>
        </Dialog>
    );
};

export default BackendErrorDialog;
